import {groupUrlsByVersion, similarityByWordsExtended} from './dataHelper';

const modelUrlCatalogs = new WeakMap();

// Catalog arrays can be edited in place. Compare their contents before reusing
// prepared URLs or matches, and let old tenant catalogs be garbage collected.
function getModelUrlCatalog(urls) {
  let catalog = modelUrlCatalogs.get(urls);
  if (!catalog || catalog.snapshot.length !== urls.length
    || catalog.snapshot.some((url, index) => url !== urls[index])) {
    catalog = {
      snapshot: urls.slice(),
      sortedUrls: groupUrlsByVersion(urls.filter(isGlbModel)),
      matches: new Map(),
    };
    modelUrlCatalogs.set(urls, catalog);
  }
  return catalog;
}

const normalizedUrlParts = new WeakMap();

// Function to normalize strings for comparison
export const normalizeString = (str) => {
  return str.toLowerCase().replace(/[\W_]+/g, ''); // Remove non-alphanumeric characters and convert to lowercase
}

// Function to split and normalize feature name or URL part for comparison
export const splitAndNormalize = (str) => {
  // Split by spaces and dashes, then normalize each part
  return str.split(/[\s\-]+/).map(normalizeString);
}

/**
 * Finds the first URL whose filename contains every normalized word in nameSearch.
 * Caches filename tokens and a word-to-URL-indices lookup for repeated style searches.
 * Searches candidates for the rarest word first, then checks all remaining words.
 * Preserves input URL order (including version priority); returns undefined if unmatched.
 */
export const getUrlByName = (nameSearch, thirdPartyAR3DUrls) => {
  const featureNameParts = splitAndNormalize(nameSearch);

  let prepared = normalizedUrlParts.get(thirdPartyAR3DUrls);
  // Catalog arrays can change in place, so identity alone cannot validate the index.
  if (!prepared || prepared.snapshot.length !== thirdPartyAR3DUrls.length
    || prepared.snapshot.some((url, index) => url !== thirdPartyAR3DUrls[index])) {
    prepared = {
      snapshot: thirdPartyAR3DUrls.slice(),
      parts: thirdPartyAR3DUrls.map(url => splitAndNormalize(url.toLowerCase().split('/').pop().split('.')[0])),
      indicesByPart: new Map(),
    };
    prepared.parts.forEach((parts, index) => {
      new Set(parts).forEach(part => {
        if (!prepared.indicesByPart.has(part)) {
          prepared.indicesByPart.set(part, []);
        }
        prepared.indicesByPart.get(part).push(index);
      });
    });
    normalizedUrlParts.set(thirdPartyAR3DUrls, prepared);
  }

  // Start with the rarest token, preserving URL/version order within candidates.
  let candidateIndices = null;
  for (const part of featureNameParts) {
    const indices = prepared.indicesByPart.get(part);
    if (!indices) {
      return undefined;
    }
    if (!candidateIndices || indices.length < candidateIndices.length) {
      candidateIndices = indices;
    }
  }
  const matchedIndex = candidateIndices.find(index => (
    featureNameParts.every(part => prepared.parts[index].includes(part))
  ));
  return matchedIndex === undefined ? undefined : thirdPartyAR3DUrls[matchedIndex];
}

export const isGlbModel = (url) => {
  if (!url || typeof url !== 'string') {
    return false;
  }

  // Remove query params before checking extension
  const cleanUrl = url.split('?')[0];

  if (cleanUrl.toLowerCase().endsWith('.glb')) {
    return true;
  }

  return false;
};

export function getUrlMatchingByColor(dbFeature, relatedParentFeature) {
  if (!dbFeature || !relatedParentFeature) {
    console.error('Invalid feature or feature properties');
    return null;
  }

  if (!Array.isArray(dbFeature.thirdPartyAR3DUrls)) {
    console.error(
      'thirdPartyAR3DUrls is not defined or not an array. Id:',
      dbFeature.id
    );
    return null;
  }

  const urls = dbFeature.thirdPartyAR3DUrls;
  const catalog = getModelUrlCatalog(urls);
  if (!catalog.sortedUrls.length) {
    return null;
  }

  const sharedFeatureName = relatedParentFeature.dbFeature.sharedFeatureName;
  const relatedParentFeatureName = relatedParentFeature.getFeatureName();
  const color = typeof relatedParentFeature.getColorName === 'function'
    ? relatedParentFeature.getColorName()
    : undefined;
  const cacheKey = JSON.stringify([sharedFeatureName, relatedParentFeatureName, color]);
  if (!catalog.matches.has(cacheKey)) {
    catalog.matches.set(cacheKey, resolveModelUrl(catalog.sortedUrls, sharedFeatureName, relatedParentFeatureName, color));
  }
  return catalog.matches.get(cacheKey);
}

function resolveModelUrl(sortedUrls, sharedFeatureName, relatedParentFeatureName, color) {
  if (!sortedUrls.length) {
    return null;
  }

  // Try to find a URL by the shared feature name
  const urlBySharedFeatureName = getUrlByName(sharedFeatureName, sortedUrls);
  if (urlBySharedFeatureName) {
    return urlBySharedFeatureName;
  }

  // Try to find a URL by the parent feature name
  const urlByFeatureName = getUrlByName(relatedParentFeatureName, sortedUrls);
  if (urlByFeatureName) {
    return urlByFeatureName;
  }

  // Try to find a URL by similarity based on the shared feature name
  const urlBySimilarity = similarityByWordsExtended(sortedUrls, sharedFeatureName);
  if (urlBySimilarity) {
    return urlBySimilarity;
  }

  // Try to find a URL by similarity based on the parent feature name
  const urlBySimilarityFromFeatureName = similarityByWordsExtended(sortedUrls, relatedParentFeatureName);
  if (urlBySimilarityFromFeatureName) {
    return urlBySimilarityFromFeatureName;
  }

  // Check if URL contains the feature name
  let matchingUrl = sortedUrls.find(url => url.includes(relatedParentFeatureName));
  if (matchingUrl) {
    return matchingUrl;
  }

  // Check if URL contains the color name
  if (color && color.length > 3) {
    matchingUrl = sortedUrls.find(url => url.includes(color));
    if (matchingUrl) {
      return matchingUrl;
    }
  }

  // If no specific color, fallback to checking for 'black' in URL
  matchingUrl = sortedUrls.find(url => url.toLowerCase().includes('black'));
  if (matchingUrl) {
    return matchingUrl;
  }

  // Use a generic "All" model only after all specific matching attempts fail.
  matchingUrl = sortedUrls.find(url => {
    const fileName = url.split('?')[0].split('/').pop();
    const fileNameParts = fileName.split(/[-_\s.%]+/).map(normalizeString);
    return fileNameParts.includes('all');
  });
  if (matchingUrl) {
    return matchingUrl;
  }

  return null;
}

